import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from '@/app/store'
import { authService } from '@/services/api/shared/auth.service'
import { navigationService } from '@/services/api/shared/navigation.service'
import { tokenManager, getTokenFingerprint } from '@/utils/tokenManager'
import { AppLoader } from '@/components/loaders/AppLoader'
import { UrlGenerator } from '@/utils/url-generator.util'
import { isPublicRoute } from '@/utils/route.util'
import { getContextTokenCookie, clearContextTokenCookie, getAccessTokenHandoffCookie, clearAccessTokenHandoffCookie } from '@/utils/cookieToken.util'
import { getUserContextFromCookie } from '@/utils/user-context-cookie.util'
import {
  setContextData,
  setContextToken,
  setUser,
  setActiveWorkspace,
  resetContextData,
  setContextRefreshing,
  setContextStable,
  setContextInvalid,
} from '@/store/slices/auth.slice'
import {
  pickDefaultWorkspace,
  readStoredWorkspace,
  sortWorkspacesByOrder,
  writeStoredWorkspace,
} from '@/utils/navigation/defaultWorkspace'
import { setPermissions, resetPermissions } from '@/store/slices/permissions.slice'
import { fetchNavigationRegistry, resetNavigation } from '@/store/slices/navigation.slice'
import { fetchRbacCatalogSnapshot } from '@/services/api/shared/rbac-catalog.service'
import { setPermissionCatalogFromSnapshot } from '@/config/rbac/permissionCatalogCache'
import type { User, UserType } from '@/types/shared/auth.types'
import { isRelaxedAutoLogoutEnabled } from '@/utils/auth-relax-auto-logout.util'
import { initSessionOrchestration } from '@/lib/session/init-session-orchestration'
import { logger } from '@/lib/logger'
import { clearStuckLoaderReloadGuard, useStuckLoadingReload } from '@/hooks/useStuckLoadingReload'
import {
  forceLogoutInvalidSession,
  isUserNotFoundApiError,
} from '@/lib/session/auth-session-invalid.util'

/** Routes where authenticated user may have no workspace context yet (access token only). */
const ROUTES_THAT_DONT_REQUIRE_CONTEXT_TOKEN = ['/accept-invites', '/branch-setup', '/onboarding']

function isAccessTokenOnlyRoute(path: string): boolean {
  if (ROUTES_THAT_DONT_REQUIRE_CONTEXT_TOKEN.includes(path)) return true
  return /^\/[^/]+\/branch-setup$/.test(path)
}

export function AuthInitializer({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const path = location.pathname
  const dispatch = useAppDispatch()
  const user = useSelector((state: RootState) => state.auth.user)
  /** When this changes (e.g. in-app branch switch), bootstrap effect must re-run to load new context policy */
  const contextTokenFromRedux = useSelector((state: RootState) => state.auth.contextToken)
  /** Needed so clearing policy (same JWT string from switch-branch) still retriggers workspace-bootstrap */
  const contextDataFromStore = useSelector((state: RootState) => state.auth.contextData)

  const isPublic = isPublicRoute(path)

  useEffect(() => {
    initSessionOrchestration()
  }, [])

  // STEP 1: Remove bad state control - keep only essential refs
  const identityFetchStartedRef = useRef(false) // Still needed for Effect 2b
  const fallbackUserFetchStartedRef = useRef(false) // Guard for Effect 2c
  const bootstrapInProgressRef = useRef(false)
  const lastBootstrapTokenRef = useRef<string | null>(null) // FIX 4: Prevent duplicate bootstraps
  const prevContextTokenRef = useRef<string | null>(null)

  useEffect(() => {
    const cur = contextTokenFromRedux ?? null
    const prev = prevContextTokenRef.current
    prevContextTokenRef.current = cur
    if (prev !== null && cur !== null && prev !== cur) {
      lastBootstrapTokenRef.current = null
      bootstrapInProgressRef.current = false
      identityFetchStartedRef.current = false
      fallbackUserFetchStartedRef.current = false
    }
  }, [contextTokenFromRedux])

  const [bootstrapping, setBootstrapping] = useState(false)
  const [bootstrapError, setBootstrapError] = useState<string | null>(null)
  const [isAuthHydrated, setIsAuthHydrated] = useState(false) // Auth state sync complete

  const latestBootstrapRef = useRef(0)
  const MAX_BOOTSTRAP_RETRIES = 3
  const RETRY_DELAY_MS = 2000

  /** Network errors, 5xx, or 429 (rate limit) — do not treat as auth failure */
  function isRetryableBootstrapError(error: any): boolean {
    if (!error?.response) return true // No response = network error (e.g. ECONNREFUSED when backend restarts)
    const status = error.response.status
    if (status === 429) return true // Rate limited; retry later instead of logging out
    return status >= 500
  }

  /**
   * Effect 0 — SessionStorage → Redux Token Sync
   * Runs on mount and route change to ensure Redux always reflects sessionStorage
   */
  useEffect(() => {
    if (isPublic) {
      setIsAuthHydrated(true)
      return
    }

    const sessionStorageToken = tokenManager.getToken('contextToken')
    
    // Sync sessionStorage token into Redux if different
    if (sessionStorageToken && sessionStorageToken !== contextTokenFromRedux) {
      dispatch(setContextToken(sessionStorageToken))
      // Important: wait for Redux sync before marking hydration complete.
      // Otherwise Effect 2 can see hydrated=true with stale/null Redux token and force logout.
      return
    }
    
    // Clear Redux state if sessionStorage is empty
    if (!sessionStorageToken && contextTokenFromRedux) {
      dispatch(resetContextData())
      dispatch(resetPermissions())
    }

    // Mark hydration complete
    setIsAuthHydrated(true)
  }, [isPublic, location.pathname, contextTokenFromRedux, dispatch])

  /**
   * Effect 1 — Cookie-to-SessionStorage Migration
   * If no sessionStorage token exists, check for temporary cookie from cross-subdomain handoff
   * This happens when user logs in on auth.vidhyantra.local and redirects to platform.vidhyantra.local
   */
  useEffect(() => {
    if (isPublic) return

    const accessHandoff = getAccessTokenHandoffCookie()
    if (accessHandoff) {
      tokenManager.setToken('accessToken', accessHandoff)
      clearAccessTokenHandoffCookie()
    }

    // Always honor cookie handoff if present (org/branch switch on same subdomain)
    const cookieToken = getContextTokenCookie()
    if (cookieToken) {
      if (cookieToken !== contextTokenFromRedux) {
        tokenManager.setToken('contextToken', cookieToken, { expires: 7 })
        dispatch(setContextToken(cookieToken))
      }
      clearContextTokenCookie()
      return
    }
  }, [isPublic, location.pathname, contextTokenFromRedux, dispatch])

  /**
   * Effect 2 — Hard logout when protected route has no token
   * CRITICAL: Separate "authentication" (access token) from "workspace context" (context token).
   * Routes that only need identity (access token in httpOnly cookie) must NOT require context token.
   */
  useEffect(() => {
    if (isPublic) return
    
    // Block until auth hydration completes
    if (!isAuthHydrated) return
    
    // Skip if already on logout page or in logout flow
    if (location.pathname === '/logout' || sessionStorage.getItem('logoutInProgress')) {
      return
    }

    // On onboarding routes, user is authenticated with access token only (no org/workspace yet).
    // Do NOT require context token here — backend allows access-token-only for these routes.
    const requiresWorkspaceContext = !isAccessTokenOnlyRoute(location.pathname)
    const sessionStorageToken = tokenManager.getToken('contextToken')
    const effectiveContextToken = contextTokenFromRedux || sessionStorageToken
    
    // Use Redux as source of truth
    if (requiresWorkspaceContext && !effectiveContextToken) {
      // Set logout in progress flag to prevent duplicate redirects
      sessionStorage.setItem('logoutInProgress', 'true')
      
      // Redirect to logout page (not direct to login)
      window.location.replace(UrlGenerator.getBaseLogoutUrl())
      return
    }

    // If token exists in storage but Redux missed this render, recover without logging out.
    if (requiresWorkspaceContext && !contextTokenFromRedux && sessionStorageToken) {
      dispatch(setContextToken(sessionStorageToken))
    }
  }, [isPublic, location.pathname, contextTokenFromRedux, isAuthHydrated, dispatch])

  /**
   * Effect 2b — Identity hydration for access-token-only routes
   * When user has access token but no context token (e.g. after full-page redirect to /onboarding),
   * fetch user identity so Redux has user before onboarding pages render.
   */
  useEffect(() => {
    if (isPublic) return

    const onIdentityOnlyRoute = isAccessTokenOnlyRoute(location.pathname)

    if (!onIdentityOnlyRoute || contextTokenFromRedux || user !== null) return
    if (identityFetchStartedRef.current) return

    identityFetchStartedRef.current = true
    navigationService
      .getUserContext(true)
      .then((userContext) => {
        if (userContext?.user) {
          dispatch(setUser(userContext.user as unknown as User))
        }
        if (!userContext?.domainNavigation?.accessibleDomains?.length) {
          const rawUserType = userContext?.user?.userType ?? ''
          const isOrgUser = rawUserType === 'ORG_USER' || rawUserType === 'ORGANIZATION'
          if (isOrgUser) {
            navigate('/onboarding?resume=true')
          } else {
            navigate('/login?stage=welcome', { replace: true })
          }
          return
        }
      })
      .catch((err) => {
        if (isUserNotFoundApiError(err)) {
          forceLogoutInvalidSession()
          return
        }
        if (isRelaxedAutoLogoutEnabled()) {
          return
        }
        window.location.replace('/login')
      })
  }, [location.pathname, user, contextTokenFromRedux, dispatch, navigate])

  /**
   * Effect 2c — Fallback user hydration for workspace routes
   * When token exists in Redux but user is missing from Redux,
   * hydrate user BEFORE bootstrap runs (breaks circular dependency).
   */
  useEffect(() => {
    if (isPublic) return

    // Use ONLY Redux token
    if (!contextTokenFromRedux || user?.id) return
    if (fallbackUserFetchStartedRef.current) return

    fallbackUserFetchStartedRef.current = true

    navigationService.getUserContext(true)
      .then((ctx) => {
        if (ctx?.user) {
          dispatch(setUser(ctx.user as unknown as User))
        }
      })
      .catch((err) => {
        if (isUserNotFoundApiError(err)) {
          forceLogoutInvalidSession()
          return
        }
        logger.error('[Auth] Failed to hydrate user')
      })
      .finally(() => {
        fallbackUserFetchStartedRef.current = false
      })
  }, [isPublic, contextTokenFromRedux, user?.id, dispatch])

  /**
   * Effect 3 — Phase-3 Bootstrap (clean, simple)
   * CRITICAL: Use Redux as single source of truth
   * Bootstrap will fetch user if needed (backend returns user in bootstrap response)
   */
  useEffect(() => {
    if (isPublic) return
    
    // Require both token AND user (fallback hydration should provide user)
    if (!contextTokenFromRedux || !user?.id) return

    const token: string = contextTokenFromRedux

    // Avoid duplicate bootstrap when workspace policy is already hydrated for this token.
    // ContextSwitcher clears contextData before swap; if the JWT string is unchanged, deps must still re-run —
    // listing contextDataFromStore in deps + requiring it here fixes stuck loaders without workspace-bootstrap.
    if (lastBootstrapTokenRef.current === token && contextDataFromStore != null) {
      return
    }

    // Skip bootstrap checks
    const skipBootstrap = sessionStorage.getItem('skipNextWorkspaceBootstrap')
    if (skipBootstrap) {
      sessionStorage.removeItem('skipNextWorkspaceBootstrap')
      dispatch(setContextStable())
      setBootstrapping(false)
      return
    }

    const skipBootstrapFingerprint = sessionStorage.getItem('skipBootstrapForToken')
    if (skipBootstrapFingerprint && getTokenFingerprint(token) === skipBootstrapFingerprint) {
      sessionStorage.removeItem('skipBootstrapForToken')
      dispatch(setContextStable())
      setBootstrapping(false)
      return
    }

    // STEP 3: Make bootstrap pure - no ref manipulation, no state tricks
    async function bootstrap(retryCount = 0) {
      // Single-flight lock
      if (bootstrapInProgressRef.current) {
        return
      }

      bootstrapInProgressRef.current = true
      setBootstrapping(true)
      setBootstrapError(null)
      dispatch(setContextRefreshing())
      dispatch(resetPermissions())

      // FIX 2: Track if we successfully applied state
      let successApplied = false

      try {
        const loginIdentityEmail = user?.email?.trim() ?? null

        // Guards
        if (!token || token.startsWith('vd_skip')) {
          setBootstrapping(false)
          bootstrapInProgressRef.current = false
          return
        }

        // Latest request wins
        const requestId = ++latestBootstrapRef.current


        // Fetch bootstrap data
        const bootstrapData = await authService.getWorkspaceBootstrap()

        // Check if still latest
        if (requestId !== latestBootstrapRef.current) {
          setBootstrapping(false)
          bootstrapInProgressRef.current = false
          return
        }

        // Handle requiresOnboarding
        if (bootstrapData.requiresOnboarding === true) {
          dispatch(setContextData(null))
          dispatch(setContextInvalid())
          setBootstrapping(false)
          bootstrapInProgressRef.current = false
          return
        }

        // Validate user identity
        if (!bootstrapData?.user?.id) {
          logger.error('[Auth] Invalid bootstrap response: missing user.id')
          dispatch(setContextInvalid())
          setBootstrapError('Invalid user identity received. Please log in again.')
          setBootstrapping(false)
          bootstrapInProgressRef.current = false
          return
        }

        // Token switch guard
        const currentToken = tokenManager.getToken('contextToken')
        if (currentToken !== token) {
          dispatch(resetPermissions())
          dispatch(setContextRefreshing())
          setBootstrapping(false)
          bootstrapInProgressRef.current = false
          return
        }

        const bu = bootstrapData.user as { id: string; email?: string | null; name?: string | null }
        const ctxType = bootstrapData.context.contextType
        const ut: UserType =
          ctxType === 'platform' ? 'PLATFORM' : ctxType === 'organization' ? 'ORGANIZATION' : 'BRANCH'
        const mergedUser: User = {
          ...(user ?? ({} as User)),
          id: bu.id,
          email: ((bu.email ?? user?.email) || '') as string,
          fullName: (bu.name ?? user?.fullName) as string | undefined,
          userType: ut,
          isEmailVerified: user?.isEmailVerified ?? true,
          isPhoneVerified: user?.isPhoneVerified ?? false,
          isActive: user?.isActive ?? true,
          createdAt: user?.createdAt ?? '',
          updatedAt: user?.updatedAt ?? '',
        }
        dispatch(setUser(mergedUser))

        // Resolve accessibleDomains
        let accessibleDomains: Array<Record<string, unknown>> = getUserContextFromCookie()?.domainNavigation?.accessibleDomains ?? []
        if (accessibleDomains.length === 0) {
          try {
            const userCtx = await navigationService.getUserContext(true)
            accessibleDomains = userCtx?.domainNavigation?.accessibleDomains ?? []
          } catch (userCtxErr) {
            if (isUserNotFoundApiError(userCtxErr)) {
              forceLogoutInvalidSession()
              return
            }
          }
        }

        // Normalize domains
        const normalizedDomains = accessibleDomains.map((d: Record<string, unknown>) => ({
          ...d,
          contextType: d.contextType ?? (d.type === 'platform' ? undefined : d.type),
        }))

        const rawWs = bootstrapData.context.workspaces
        const contextToSet = {
          ...bootstrapData.context,
          accessibleDomains: normalizedDomains as import('@/types/shared/auth.types').AccessibleDomain[],
          platformMenuKeys: bootstrapData.context.platformMenuKeys ?? [],
          ...(Array.isArray(rawWs) && rawWs.length > 0
            ? { workspaces: sortWorkspacesByOrder([...rawWs]) }
            : {}),
        }

        // Apply context and permissions
        dispatch(setContextData(contextToSet))

        if (contextToSet.contextType === 'branch' && Array.isArray(contextToSet.workspaces) && contextToSet.workspaces.length > 0) {
          const stored = readStoredWorkspace(contextToSet.organizationId, contextToSet.branchId)
          const next =
            stored && contextToSet.workspaces.includes(stored) ? stored : pickDefaultWorkspace(contextToSet.workspaces)
          if (next) {
            dispatch(setActiveWorkspace(next))
            writeStoredWorkspace(contextToSet.organizationId, contextToSet.branchId, next)
          } else {
            dispatch(setActiveWorkspace(null))
          }
        } else {
          dispatch(setActiveWorkspace(null))
        }
        
        if (token !== contextTokenFromRedux) {
          dispatch(setContextToken(token))
        }

        const navScope = bootstrapData.context.contextType
        if (navScope !== 'platform' && navScope !== 'organization' && navScope !== 'branch') {
          throw new Error(`Invalid bootstrap context scope: ${String(navScope)}`)
        }

        try {
          await dispatch(fetchNavigationRegistry(navScope)).unwrap()
          // Warm DB-backed permission catalog (server Redis cache; one fetch per session).
          void fetchRbacCatalogSnapshot()
            .then(setPermissionCatalogFromSnapshot)
            .catch(() => undefined)
        } catch {
          dispatch(resetContextData())
          dispatch(resetNavigation())
          dispatch(setActiveWorkspace(null))
          dispatch(resetPermissions())
          throw new Error('Failed to load PCS-filtered navigation registry for your workspace.')
        }

        if (
          typeof import.meta !== 'undefined' &&
          import.meta.env.DEV &&
          loginIdentityEmail &&
          bu.email &&
          loginIdentityEmail.toLowerCase() !== String(bu.email).trim().toLowerCase()
        ) {
          throw new Error(
            `[Auth] LOGIN_USER_EMAIL_MISMATCH: session user ${loginIdentityEmail} vs bootstrap user ${bu.email}`
          )
        }

        const permissions = bootstrapData.context.permissions ?? []

        dispatch(
          setPermissions({
            permissions,
            profileType: contextToSet.profileType,
            activeProfileId: contextToSet.activeProfileId,
            userType: ut,
            contextType: contextToSet.contextType,
            organizationSlug: contextToSet.organizationSlug,
            branchSlug: contextToSet.branchSlug,
          })
        )

        if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
          logger.info('[Auth] workspace bootstrap OK', {
            loginUserEmail: mergedUser.email,
            contextBootstrapEmail: bu.email ?? null,
            userId: mergedUser.id,
            LOGIN_USER_EQUALS_BOOTSTRAP_EMAIL:
              !(bu.email && mergedUser.email) || String(bu.email).toLowerCase() === String(mergedUser.email).toLowerCase(),
            permissionCount: permissions.length,
          })
        }

        successApplied = true

        dispatch(setContextStable())

        lastBootstrapTokenRef.current = token

        setBootstrapError(null)
        clearStuckLoaderReloadGuard()

        sessionStorage.removeItem('contextSelectionInProgress')
        sessionStorage.removeItem('logoutInProgress')

      } catch (error: any) {
        const status = error?.response?.status
        
        // FIX 1: ONLY reset state on auth failures (not on network/registry errors)
        if (status === 401 || status === 403) {
          logger.error('[Auth] Bootstrap auth failure', status)
          dispatch(resetContextData())
          dispatch(resetPermissions())
          dispatch(resetNavigation())
        } else if (!successApplied) {
          dispatch(resetContextData())
          dispatch(resetPermissions())
          dispatch(resetNavigation())
        }

        const msg = typeof error?.message === 'string' ? error.message : ''

        // Skip if logout in progress
        if (sessionStorage.getItem('logoutInProgress')) {
          return
        }

        // Auth failures
        if (status === 401 || status === 403) {
          if (isUserNotFoundApiError(error)) {
            forceLogoutInvalidSession()
            return
          }
          if (isRelaxedAutoLogoutEnabled()) {
            setBootstrapError(`Workspace bootstrap returned ${status}. Session kept (relaxed auto-logout).`)
            return
          }
          sessionStorage.setItem('logoutInProgress', 'true')
          window.location.replace(UrlGenerator.getBaseLogoutUrl())
          return
        }

        if (msg.includes('LOGIN_USER_EMAIL_MISMATCH') || msg.includes('PCS-filtered navigation registry')) {
          setBootstrapError(msg)
          return
        }

        // Retryable errors (network/server)
        if (isRetryableBootstrapError(error) && retryCount < MAX_BOOTSTRAP_RETRIES) {
          // STEP 4: Simple retry - just retry the API call
          setTimeout(() => bootstrap(retryCount + 1), RETRY_DELAY_MS)
          return
        }

        // Max retries reached or other error
        setBootstrapError('Server is temporarily unavailable. Please try again.')

      } finally {
        // STEP 3: Ensure lock ALWAYS releases (defensive)
        setBootstrapping(false)
        bootstrapInProgressRef.current = false
      }
    }

    bootstrap(0)
  }, [contextTokenFromRedux, contextDataFromStore, user?.id, isPublic, dispatch])

  /**
   * Render Logic
   * CRITICAL: Use Redux token (already synced by Effect 0).
   * On onboarding routes (onboarding, accept-invites) we allow render without contextToken —
   * user is authenticated with access token only; bootstrap is skipped (Effect 3 returns early when !contextToken).
   */
  const onIdentityOnlyRoute = isAccessTokenOnlyRoute(location.pathname)

  const sessionContextToken = isPublic ? null : tokenManager.getToken('contextToken')
  const effectiveContextToken = contextTokenFromRedux ?? sessionContextToken

  const showAuthLoader =
    !isPublic &&
    (!isAuthHydrated ||
      (onIdentityOnlyRoute && !effectiveContextToken && user === null) ||
      (!effectiveContextToken && !onIdentityOnlyRoute) ||
      (Boolean(effectiveContextToken) && !user?.id && !onIdentityOnlyRoute) ||
      (!onIdentityOnlyRoute &&
        Boolean(effectiveContextToken) &&
        Boolean(user?.id) &&
        contextDataFromStore == null &&
        !bootstrapError) ||
      bootstrapping)

  useStuckLoadingReload(showAuthLoader && !bootstrapError)

  if (isPublic) {
    return <>{children}</>
  }

  // Wait for auth hydration before making any UI decisions
  if (!isAuthHydrated) {
    return (
      <AppLoader
        action="load"
        title="Initializing..."
        description="Preparing your session"
        entityName="session"
      />
    )
  }

  if (onIdentityOnlyRoute && !effectiveContextToken && user === null) {
    return (
      <AppLoader
        action="load"
        title="Loading your account..."
        description="Preparing your workspace"
        entityName="account"
      />
    )
  }

  if (!effectiveContextToken && !onIdentityOnlyRoute) {
    return (
      <AppLoader
        action="load"
        title="Loading your workspace..."
        description="Fetching your data"
        entityName="workspace"
      />
    )
  }

  if (effectiveContextToken && !user?.id && !onIdentityOnlyRoute) {
    return (
      <AppLoader
        action="load"
        title="Loading your workspace..."
        description="Loading your profile"
        entityName="account"
      />
    )
  }

  if (bootstrapError) {
    const handleRetry = () => {
      // Reset lock and trigger effect re-run
      bootstrapInProgressRef.current = false
      setBootstrapError(null)
      setBootstrapping(true)
      // Force effect re-run by toggling contextToken in Redux
      if (contextTokenFromRedux) {
        dispatch(setContextToken(contextTokenFromRedux))
      }
    }

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6">
        <p className="text-center text-muted-foreground">{bootstrapError}</p>
        <button
          type="button"
          onClick={handleRetry}
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    )
  }

  if (bootstrapping) {
    return (
      <AppLoader
        action="load"
        title="Loading your workspace..."
        description="Setting up features and permissions"
        entityName="workspace"
      />
    )
  }

  if (
    !onIdentityOnlyRoute &&
    effectiveContextToken &&
    user?.id &&
    contextDataFromStore == null &&
    !bootstrapError
  ) {
    return (
      <AppLoader
        action="load"
        title="Loading your workspace..."
        description="Setting up features and permissions"
        entityName="workspace"
      />
    )
  }

  return <>{children}</>
}

export default AuthInitializer
